"use client";

import { cn } from "@universe/ui/lib/utils";
import { motion } from "framer-motion";
import { ArrowRight, X } from "lucide-react";
import { useTranslations } from "next-intl";

interface PendingItemsBadgeProps {
  unconfirmedCount: number;
  onClick: () => void;
  isVisible: boolean;
  isTabActive?: boolean;
  onDismiss?: () => void;
}

export default function PendingItemsBadge({
  unconfirmedCount,
  onClick,
  isVisible,
  isTabActive = false,
  onDismiss,
}: PendingItemsBadgeProps) {
  const tCommon = useTranslations("common");

  if (!isVisible) {
    return null;
  }

  return (
    <div className="relative mb-4 inline-block">
      <motion.button
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.2 }}
        onClick={onClick}
        className="inline-flex items-center justify-between gap-3 rounded-full bg-red-50 border border-red-200 pl-1.5 pr-2 py-1.5 text-left transition-all hover:bg-red-100 hover:shadow-[0_6px_12px_rgba(239,68,68,0.3)] cursor-pointer"
        style={
          {
            boxShadow: "0 6px 15px rgba(239, 68, 68, 0.2)",
          } as React.CSSProperties
        }
      >
        <div className="flex items-center gap-2.5">
          <div
            className={cn(
              "flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold",
              isTabActive ? "bg-red-200 text-red-800" : "bg-red-500 text-white"
            )}
          >
            {unconfirmedCount}
          </div>
          <span className="font-medium text-red-800 text-sm">
            {tCommon("itemsRequireAttention", { count: unconfirmedCount })}
          </span>
        </div>
        <ArrowRight className="h-4 w-4 text-red-600" />
      </motion.button>
      {onDismiss && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          className="absolute top-[-2px] right-[-22px] flex h-5 w-5 items-center justify-center rounded-full border border-input bg-brand-primary/10 text-gray-700 hover:bg-brand-primary/40 hover:text-gray-700 transition-colors cursor-pointer shadow-sm"
          aria-label="Dismiss"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}
